import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchActionHistory, undoAction } from '../services/activityLogService';
import useUser from '../auth/useUser';
import MultiSelect from '../components/MultiSelect';
import './Suppliers.css';

/* ---------- Helpers ---------- */

const fmtDate = (d) => {
  if (!d) return '\u2014';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return '\u2014';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const ACTION_TYPE_LABELS = {
  single_edit:    { emoji: '\u270F\uFE0F',  label: 'Edit' },
  bulk_edit:      { emoji: '\uD83D\uDCE6', label: 'Bulk' },
  status_change:  { emoji: '\uD83D\uDD04', label: 'Status' },
  product_add:    { emoji: '\u2795',        label: 'Add' },
  soft_delete:    { emoji: '\uD83D\uDDD1\uFE0F',  label: 'Delete' },
  reactivate:     { emoji: '\u267B\uFE0F',  label: 'Reactivate' },
  supplier_edit:  { emoji: '\uD83C\uDFE2', label: 'Supplier' },
  csv_upload:     { emoji: '\uD83D\uDCC4', label: 'CSV Upload' },
  undo:           { emoji: '\u21A9\uFE0F',  label: 'Undo' },
};

const fmtActionType = (type) => {
  const entry = ACTION_TYPE_LABELS[type];
  if (!entry) return type || '\u2014';
  return `${entry.emoji} ${entry.label}`;
};

const actionTypeOptions = (types) =>
  (types || []).map((t) => {
    const entry = ACTION_TYPE_LABELS[t];
    return entry ? `${entry.emoji} ${entry.label}:::${t}` : t;
  });

const STATUS_COLORS = {
  active: 'var(--teal)',
  undone: '#9ca3af',
};

/* ---------- Component ---------- */

export default function ActivityLog() {
  const { displayName: editorName } = useUser();
  const location = useLocation();

  /* Data state */
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [filterOptions, setFilterOptions] = useState({ user_names: [], action_types: [] });
  const [err, setErr] = useState('');

  /* Filters */
  const [filters, setFilters] = useState({
    user_name: [],
    action_type: [],
    status: [],
    summary_search: '',
  });

  /* Pagination */
  const PAGE_SIZE = 50;
  const [page, setPage] = useState(1);

  /* UI state */
  const [toast, setToast] = useState(null);
  const [undoConfirm, setUndoConfirm] = useState(null);
  const [undoing, setUndoing] = useState(false);

  /* Toast auto-dismiss */
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  /* Build query params */
  const queryParams = useMemo(() => {
    const p = { page, limit: PAGE_SIZE };
    if (filters.user_name.length) p.user_name = filters.user_name.join(',');
    if (filters.action_type.length) p.action_type = filters.action_type.join(',');
    if (filters.status.length) p.status = filters.status.map(s => s.toLowerCase()).join(',');
    if (filters.summary_search.trim()) p.summary_search = filters.summary_search.trim();
    return p;
  }, [page, filters]);

  /* Fetch data */
  const load = useCallback(async () => {
    try {
      const data = await fetchActionHistory(queryParams);
      setRows(data.rows || []);
      setTotal(data.total || 0);
      if (data.filter_options) setFilterOptions(data.filter_options);
      setErr('');
    } catch (e) {
      setErr(e?.response?.data?.error || 'Failed to load action history');
      setRows([]);
    }
  }, [queryParams]);

  useEffect(() => { load(); }, [load]);

  /* Reset page on filter change */
  useEffect(() => { setPage(1); }, [filters]);

  /* Pagination helpers */
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const pageEnd = Math.min(pageStart + PAGE_SIZE, total);

  /* Action type MultiSelect options (with emoji labels) */
  const actionTypeMultiOptions = useMemo(() => {
    return (filterOptions.action_types || []).map((t) => {
      const entry = ACTION_TYPE_LABELS[t];
      return entry ? `${entry.emoji} ${entry.label}` : t;
    });
  }, [filterOptions.action_types]);

  /* Map display label back to raw action_type key */
  const actionTypeLabelToKey = useMemo(() => {
    const map = {};
    (filterOptions.action_types || []).forEach((t) => {
      const entry = ACTION_TYPE_LABELS[t];
      const label = entry ? `${entry.emoji} ${entry.label}` : t;
      map[label] = t;
    });
    return map;
  }, [filterOptions.action_types]);

  const actionTypeKeyToLabel = useMemo(() => {
    const map = {};
    (filterOptions.action_types || []).forEach((t) => {
      const entry = ACTION_TYPE_LABELS[t];
      const label = entry ? `${entry.emoji} ${entry.label}` : t;
      map[t] = label;
    });
    return map;
  }, [filterOptions.action_types]);

  const [conflictInfo, setConflictInfo] = useState(null);
  const [undoResult, setUndoResult] = useState(null);

  /* Undo handler */
  const handleUndo = async () => {
    if (!undoConfirm) return;
    setUndoing(true);
    try {
      const res = await undoAction(undoConfirm.id, editorName);
      const isCsvUndo = undoConfirm.action_type === 'csv_upload' && res.reverted_inserts != null;
      if (isCsvUndo) {
        setUndoResult(res);
      } else {
        setToast({ type: 'ok', msg: `Action #${undoConfirm.id} has been undone successfully.` });
      }
      setUndoConfirm(null);
      load();
    } catch (e) {
      const resp = e?.response?.data;
      const code = resp?.code || 'UNKNOWN';
      const msg = resp?.error || 'Failed to undo action';
      setUndoConfirm(null);
      if (code === 'CONFLICT' && resp?.conflicts) {
        setConflictInfo({ ids: resp.conflicts, message: msg });
      } else {
        setToast({ type: 'error', msg });
      }
    } finally {
      setUndoing(false);
    }
  };

  /* Undo button renderer */
  const renderUndoBtn = (row) => {
    if (row.status === 'undone') {
      return (
        <button type="button" className="sup-btn" disabled style={{ opacity: 0.5, fontSize: 11, padding: '3px 8px' }}>
          Already Undone
        </button>
      );
    }
    if (!row.is_undoable) {
      return (
        <span title="This action must be rolled back manually">
          <button type="button" className="sup-btn" disabled style={{ opacity: 0.5, fontSize: 11, padding: '3px 8px' }}>
            Manual Rollback
          </button>
        </span>
      );
    }
    if (row.expires_at && new Date(row.expires_at) < new Date()) {
      return (
        <button type="button" className="sup-btn" disabled style={{ opacity: 0.5, fontSize: 11, padding: '3px 8px' }}>
          Expired
        </button>
      );
    }
    return (
      <button
        type="button"
        className="sup-btn primary"
        style={{ fontSize: 11, padding: '3px 8px' }}
        onClick={() => setUndoConfirm(row)}
      >
        <i className="bi bi-arrow-counterclockwise me-1"></i>Undo
      </button>
    );
  };

  return (
    <div className="sup-page">
      {/* Header */}
      <div className="mb-3">
        <h2 className="sup-heading mb-1">Activity Log</h2>
        <div className="sup-breadcrumb">FocusOne &middot; System Management &middot; Activity Log</div>
      </div>

      {err && <div className="sup-error-box mb-3">{err}</div>}

      {/* Loading skeleton */}
      {!rows && !err && (
        <div className="sup-table-wrap p-4">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="sup-skeleton mb-2" style={{ height: 28, width: '100%' }} />
          ))}
        </div>
      )}

      {rows && (
        <>
          {/* Stats bar */}
          <div className="sup-statsbar">
            <span className="sup-stat-gray">Total: {total.toLocaleString('en-US')} actions</span>
          </div>

          {/* Filters */}
          <div className="sup-filters-row" style={{ marginBottom: 10, flexWrap: 'wrap' }}>
            <MultiSelect
              label="User"
              options={filterOptions.user_names || []}
              values={filters.user_name}
              onChange={(v) => setFilters((f) => ({ ...f, user_name: v }))}
              placeholder="All Users"
              minWidth={180}
            />
            <MultiSelect
              label="Action Type"
              options={actionTypeMultiOptions}
              values={filters.action_type.map((k) => actionTypeKeyToLabel[k] || k)}
              onChange={(v) =>
                setFilters((f) => ({
                  ...f,
                  action_type: v.map((label) => actionTypeLabelToKey[label] || label),
                }))
              }
              placeholder="All Types"
              minWidth={180}
            />
            <MultiSelect
              label="Status"
              options={['Active', 'Undone']}
              values={filters.status}
              onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
              placeholder="All Statuses"
              minWidth={140}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                Summary Search
              </label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search summary..."
                value={filters.summary_search}
                onChange={(e) => setFilters((f) => ({ ...f, summary_search: e.target.value }))}
                style={{ fontSize: 13, minWidth: 200 }}
              />
            </div>
          </div>

          {/* Showing info */}
          <div style={{ color: '#6B7280', fontSize: 13, marginBottom: 8 }}>
            Showing {total === 0 ? 0 : pageStart + 1}&ndash;{pageEnd} of {total.toLocaleString('en-US')} actions
          </div>

          {/* Table */}
          <div className="sup-table-wrap">
            <table className="sup-table">
              <thead>
                <tr>
                  <th style={{ width: 60 }}>ID</th>
                  <th style={{ width: 170 }}>TIMESTAMP</th>
                  <th style={{ width: 120 }}>TYPE</th>
                  <th>SUMMARY</th>
                  <th style={{ width: 60 }}>ROWS</th>
                  <th style={{ width: 130 }}>USER</th>
                  <th style={{ width: 90 }}>STATUS</th>
                  <th style={{ width: 120 }}>UNDO</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', color: 'var(--t3)', padding: 32 }}>
                      No actions found.
                    </td>
                  </tr>
                )}
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>{r.id}</td>
                    <td>{fmtDate(r.created_at)}</td>
                    <td>{fmtActionType(r.action_type)}</td>
                    <td style={{ maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.summary || '\u2014'}
                    </td>
                    <td style={{ textAlign: 'center' }}>{r.affected_rows ?? '\u2014'}</td>
                    <td>{r.user_name || '\u2014'}</td>
                    <td>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 10px',
                          borderRadius: 12,
                          fontSize: 11,
                          fontWeight: 600,
                          color: '#fff',
                          background: STATUS_COLORS[r.status] || '#9ca3af',
                          textTransform: 'capitalize',
                        }}
                      >
                        {r.status}
                      </span>
                    </td>
                    <td>{renderUndoBtn(r)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            <nav aria-label="pagination" style={{ flexShrink: 0, display: 'flex', gap: 4, alignItems: 'center' }}>
              <button
                type="button"
                disabled={safePage === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                style={{
                  fontSize: 12,
                  color: safePage === 1 ? 'var(--t3)' : 'var(--teal)',
                  border: '1px solid ' + (safePage === 1 ? '#dee2e6' : 'var(--teal)'),
                  background: 'var(--card)',
                  borderRadius: 6,
                  padding: '5px 10px',
                  cursor: safePage === 1 ? 'not-allowed' : 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                Previous
              </button>
              {(() => {
                const win = 5;
                let s = Math.max(1, safePage - Math.floor(win / 2));
                let e = Math.min(pageCount, s + win - 1);
                s = Math.max(1, e - win + 1);
                const nums = [];
                for (let i = s; i <= e; i++) nums.push(i);
                return nums.map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setPage(n)}
                    style={{
                      fontSize: 12,
                      color: safePage === n ? 'white' : 'var(--teal)',
                      border: '1px solid var(--teal)',
                      background: safePage === n ? 'var(--teal)' : 'var(--card)',
                      borderRadius: 6,
                      padding: '5px 10px',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      fontWeight: safePage === n ? 600 : 400,
                    }}
                  >
                    {n}
                  </button>
                ));
              })()}
              <button
                type="button"
                disabled={safePage === pageCount}
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                style={{
                  fontSize: 12,
                  color: safePage === pageCount ? 'var(--t3)' : 'var(--teal)',
                  border: '1px solid ' + (safePage === pageCount ? '#dee2e6' : 'var(--teal)'),
                  background: 'var(--card)',
                  borderRadius: 6,
                  padding: '5px 10px',
                  cursor: safePage === pageCount ? 'not-allowed' : 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                Next
              </button>
            </nav>
            <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--t3)', whiteSpace: 'nowrap' }}>
              Showing {total === 0 ? 0 : pageStart + 1}&ndash;{pageEnd} of {total.toLocaleString('en-US')} {total === 1 ? 'action' : 'actions'}
            </span>
          </div>
        </>
      )}

      {/* Undo confirmation modal */}
      {undoConfirm && (
        <div className="sup-modal-backdrop" onClick={() => !undoing && setUndoConfirm(null)}>
          <div className="sup-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="sup-modal-head">
              <h5 className="sup-modal-title mb-0">Confirm Undo</h5>
              <button type="button" className="sup-modal-close" onClick={() => !undoing && setUndoConfirm(null)}>
                &times;
              </button>
            </div>
            <div className="sup-modal-body">
              <p style={{ margin: 0, fontSize: 14 }}>
                Are you sure you want to undo action <strong>#{undoConfirm.id}</strong>?
              </p>
              <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--t3)' }}>
                {undoConfirm.summary}
              </p>
              <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--t3)' }}>
                This will revert {undoConfirm.affected_rows ?? 0} affected row(s) to their previous values.
              </p>
            </div>
            <div className="sup-modal-foot">
              <button
                type="button"
                className="sup-btn"
                disabled={undoing}
                onClick={() => setUndoConfirm(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="sup-btn primary"
                disabled={undoing}
                onClick={handleUndo}
              >
                {undoing ? 'Undoing...' : 'Yes, Undo'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Conflict modal */}
      {conflictInfo && (
        <div className="sup-modal-backdrop" onClick={() => setConflictInfo(null)}>
          <div className="sup-modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
            <div className="sup-modal-head">
              <h5 className="sup-modal-title mb-0" style={{ color: '#dc2626' }}>Undo Conflict</h5>
              <button type="button" className="sup-modal-close" onClick={() => setConflictInfo(null)}>×</button>
            </div>
            <div className="sup-modal-body" style={{ fontSize: 14 }}>
              <p style={{ marginBottom: 12 }}>{conflictInfo.message}</p>
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '10px 14px', fontSize: 13 }}>
                <strong>Conflicting row IDs:</strong> {conflictInfo.ids.join(', ')}
              </div>
              <p style={{ marginTop: 12, color: 'var(--t3)', fontSize: 13 }}>Please review these rows manually before attempting to undo.</p>
            </div>
            <div className="sup-modal-foot">
              <button type="button" className="sup-btn" onClick={() => setConflictInfo(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Undo Result modal */}
      {undoResult && (
        <div className="sup-modal-backdrop" onClick={() => setUndoResult(null)}>
          <div className="sup-modal" style={{ maxWidth: 500 }} onClick={(e) => e.stopPropagation()}>
            <div className="sup-modal-head">
              <h5 className="sup-modal-title mb-0">Upload Undone</h5>
              <button type="button" className="sup-modal-close" onClick={() => setUndoResult(null)}>&times;</button>
            </div>
            <div className="sup-modal-body" style={{ fontSize: 14 }}>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                <li>Reverted: <strong>{undoResult.reverted_inserts ?? 0}</strong> inserts</li>
                <li>Restored: <strong>{undoResult.reverted_merges ?? 0}</strong> merge updates</li>
                <li>Skipped: <strong>{undoResult.skipped_count ?? 0}</strong> rows</li>
              </ul>
              {(undoResult.skipped_conflicts?.length > 0) && (
                <div style={{ marginTop: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '10px 14px', fontSize: 13 }}>
                  <strong>Skipped rows:</strong>
                  <ul style={{ margin: '6px 0 0', paddingLeft: 16 }}>
                    {undoResult.skipped_conflicts.map((s, i) => (
                      <li key={i}>Product #{s.id} &mdash; {s.reason === 'edited_after_upload' ? 'edited after upload' : 'already deleted'}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="sup-modal-foot">
              <button type="button" className="sup-btn primary" onClick={() => setUndoResult(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`sup-toast ${toast.type === 'error' ? 'error' : ''}`}>{toast.msg}</div>
      )}
    </div>
  );
}
